/**
 * ISSUES DISPLAY
 * Pure rule : the issues of the same question are displayed together ("Topic · question"),
 * one line when it has a single problem, a list when it has several. The count of issues does not change.
 */

export interface DisplayIssue {
  id: string
  kind?: string //Type of the problem (see TemplateIssueKind) : the problems of the same type can be displayed together
  severity: "error" | "warning"
  label: string
  description: string
  group?: string
  intro?: string //Start of the line when the detail is a list
  details?: string[] //Lines under the issue
  target?: string //Where to fix it (checks of a project)
  ignored?: boolean //Warning ignored in the editor (displayed greyed)
}

export interface IssueGroup {
  key: string
  title: string
  items: { id: string; text: string; intro?: string; details?: string[]; isAi?: boolean }[]
}

//Types of problem that come from the AI of the assistant : a small logo is shown beside them
export const AI_ISSUE_KINDS: string[] = ["reverse-suspect"]

//The flag is only there for the issues of the AI
const aiFlag = (issue: DisplayIssue) => (AI_ISSUE_KINDS.includes(issue.kind || "") ? { isAi: true } : {})

//Keeps the order of the first occurrence of each group
export function groupIssues(issues: DisplayIssue[]): IssueGroup[] {
  const groups: IssueGroup[] = []
  issues.forEach((issue) => {
    const key = issue.group ?? issue.id
    const existing = groups.find((x) => x.key === key)
    if (existing) {
      existing.items.push({
        id: issue.id,
        text: issue.description,
        intro: issue.intro,
        details: issue.details,
        ...aiFlag(issue)
      })
    } else {
      groups.push({
        key,
        title: issue.group ?? issue.label,
        items: [{ id: issue.id, text: issue.description, intro: issue.intro, details: issue.details, ...aiFlag(issue) }]
      })
    }
  })
  return groups
}

//Past this number of problems, the project displays them by type (one line per type, folded)
export const ISSUES_GROUP_THRESHOLD = 8

//Groups of the problems of the same type, in the order of the first occurrence of each type
export function groupIssuesByKind(issues: DisplayIssue[]): { kind: string; issues: DisplayIssue[] }[] {
  const groups: { kind: string; issues: DisplayIssue[] }[] = []
  issues.forEach((issue) => {
    const kind = issue.kind || "other"
    const existing = groups.find((x) => x.kind === kind)
    if (existing) existing.issues.push(issue)
    else groups.push({ kind, issues: [issue] })
  })
  return groups
}
